import React, { useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sortedBlogPosts, getFeaturedPost } from '../60-blog/blogData';

interface ArticlesSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const allNonFeatured = sortedBlogPosts.filter(p => !p.featured);

const ArticlesSection: React.FC<ArticlesSectionProps> = ({ highlightColor }) => {
    const featured = getFeaturedPost();
    const scrollRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const directionRef = useRef<'left' | 'right' | null>(null);

    const scrollSpeed = 2; // px per frame

    const tick = useCallback(() => {
        if (!scrollRef.current || !directionRef.current) return;
        const el = scrollRef.current;
        const delta = directionRef.current === 'right' ? scrollSpeed : -scrollSpeed;
        el.scrollLeft += delta;
        rafRef.current = requestAnimationFrame(tick);
    }, []);

    const startScroll = useCallback((dir: 'left' | 'right') => {
        directionRef.current = dir;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
    }, [tick]);

    const stopScroll = useCallback(() => {
        directionRef.current = null;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid-dots overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="flex justify-between items-center mb-12 observe-fade opacity-0">
                    <h2 className="text-4xl md:text-5xl font-bold text-white">
                        Latest articles
                    </h2>
                    <Link
                        to="/blog"
                        className="text-gray-300 hover:text-white transition-colors text-sm font-medium"
                    >
                        View All →
                    </Link>
                </div>

                {/* Featured article — mirrors the blog listing page featured card */}
                {featured && (
                    <div className="mb-10 observe-fade opacity-0">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Featured</p>
                    <Link
                        to={`/blog/${featured.slug}`}
                        className="block group"
                    >
                        <div className="bg-neutral-900 border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 overflow-hidden md:flex">
                            <div className="md:w-2/5 h-56 md:h-auto">
                                <img
                                    src={featured.heroImage}
                                    alt={featured.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-8 md:p-12 md:w-3/5">
                                <span className="text-xs uppercase tracking-widest text-gray-500">{featured.category}</span>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-3 group-hover:text-gray-200 transition-colors">
                                    {featured.title}
                                </h3>
                                <p className="text-gray-400 text-base max-w-3xl mb-4">{featured.subtitle}</p>
                                <span className="text-sm text-gray-500">{formatDate(featured.date)}</span>
                            </div>
                        </div>
                    </Link>
                    </div>
                )}

                {/* Hover-to-scroll article carousel */}
                <div className="relative">
                    {/* Left arrow */}
                    <div
                        onMouseEnter={() => startScroll('left')}
                        onMouseLeave={stopScroll}
                        className="absolute left-0 inset-y-0 z-10 flex items-center cursor-pointer opacity-40 hover:opacity-100 transition-opacity duration-300"
                    >
                        <div className="bg-black/60 backdrop-blur-sm border border-neutral-700 p-3 ml-2">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </div>
                    </div>

                    {/* Right arrow */}
                    <div
                        onMouseEnter={() => startScroll('right')}
                        onMouseLeave={stopScroll}
                        className="absolute right-0 inset-y-0 z-10 flex items-center cursor-pointer opacity-40 hover:opacity-100 transition-opacity duration-300"
                    >
                        <div className="bg-black/60 backdrop-blur-sm border border-neutral-700 p-3 mr-2">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    {/* Scrollable track — hidden scrollbar */}
                    <div
                        ref={scrollRef}
                        className="flex gap-6 overflow-x-auto"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        <style>{`.articles-scroll::-webkit-scrollbar { display: none; }`}</style>
                        {allNonFeatured.map((post) => (
                            <Link
                                key={post.slug}
                                to={`/blog/${post.slug}`}
                                className="block group shrink-0"
                                style={{ width: '320px' }}
                            >
                                <div className="bg-neutral-900 h-full flex flex-col border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 group-hover:bg-neutral-900/80 overflow-hidden">
                                    <div className="h-40 overflow-hidden">
                                        <img
                                            src={post.heroImage}
                                            alt={post.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-widest">
                                            {post.category}
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-3 group-hover:text-gray-200 transition-colors leading-snug">
                                            {post.title}
                                        </h3>
                                        <p className="text-gray-300 text-sm leading-relaxed flex-1">
                                            {post.subtitle}
                                        </p>
                                        <span className="text-xs text-gray-600 mt-4">{formatDate(post.date)}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ArticlesSection;
